import "./cockpit.css";

import { apm } from "@elastic/apm-rum";
import { CaptureSystem } from "@inovitas/infra3dsdk/dist/types/mm/api/utils/capturesystem";
import { Node } from "@inovitas/infra3dsdk/dist/types/mm/graph/Node";
import {
  Contrast,
  NavigateBefore,
  NavigateNext,
  PauseCircleOutline,
  PlayCircleOutline,
  PushPin,
  PushPinOutlined,
  Speed,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Popover,
  Skeleton,
  Slider,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { CockpitCameraSelection } from "shared/common/utils/icons/icons";
import PopoverWithPointerForwarding from "shared/ui/muiComponentWrappers/PopoverWithPointerForwarding";
import Axisscale from "./Axisscale";
import CameraPositions from "./sensorSelection/CameraPositions";

export interface CameraNode {
  frame: {
    streamtype: string;
    capturesystemID: string;
    capturesystem: {
      sensorgroup: number;
      orientation: number;
    };
  };
}

export default function Cockpit() {
  const viewer = defaultStore((state) => state.viewer);
  const showStreetMap = defaultStore((state) => state.showStreetMap);
  const cockpitPosition = defaultStore((state) => state.cockpitPosition);

  //states
  const [pausePlay, setPausePlay] = useState<boolean>(false);
  const [directionBackward, setDirectionBackward] = useState<boolean>(false);
  const [speedValue, setSpeedValue] = useState<number>(0.62);
  const [brightnessValue, setBrightnessValue] = useState<number>(1);
  const [camerasArray, setCamerasArray] = useState<CameraNode[]>([]);
  const [activeCamera, setActiveCamera] = useState<CaptureSystem>();
  const [pinCamera, setPinCamera] = useState<boolean>(false);

  const { t } = useTranslation();

  //set default values for speed, cameras, direction etc.
  useEffect(() => {
    if (viewer !== undefined) {
      viewer.on("cameraconfigurationchanged", (nodes: Node[]) => {
        const streamdesc = nodes.map((node) => {
          if (
            viewer.getScene().getCurrentGeoframe() !== null &&
            node.key === viewer.getScene().getCurrentGeoframe().key
          ) {
            setActiveCamera(node.frame.capturesystem);
          }
          return {
            frame: {
              streamtype: node.frame.streamtype,
              capturesystemID: node.frame.capturesystemID,
              capturesystem: {
                sensorgroup: node.frame.capturesystem.sensorgroup,
                orientation: node.frame.capturesystem.orientation,
              },
            },
          };
        });
        setCamerasArray(streamdesc);
      });

      viewer.on("nodechanged", (node) => {
        setActiveCamera(node.frame.capturesystem);
      });

      viewer.on("playingchanged", (playing: boolean) => {
        setPausePlay(playing);
      });

      viewer.on("directionchanged", (direction: number) => {
        if (direction === 0) {
          setDirectionBackward(false);
        } else if (direction === 1) {
          setDirectionBackward(true);
        }
      });

      viewer.on("speedchanged", (speed: number) => {
        if (speed !== 0) {
          setSpeedValue(speed);
        }
      });
    }
  }, [viewer]);

  //change speed button and popover
  const [anchorElSpeed, setAnchorElSpeed] = useState<HTMLButtonElement | null>(
    null
  );
  const handleClickSpeed = (event: MouseEvent<HTMLButtonElement>) => {
    if (!anchorElSpeed) {
      setAnchorElSpeed(event.currentTarget);
    }
  };
  const handleCloseSpeed = () => {
    setAnchorElSpeed(null);
  };
  const speedPopover = Boolean(anchorElSpeed);

  //change contrast button and popover
  const [anchorElContrast, setAnchorElContrast] =
    useState<HTMLButtonElement | null>(null);
  const handleClickContrast = (event: MouseEvent<HTMLButtonElement>) => {
    if (!anchorElContrast) {
      setAnchorElContrast(event.currentTarget);
    }
  };
  const handleCloseContrast = () => {
    setAnchorElContrast(null);
  };
  const contrastPopover = Boolean(anchorElContrast);

  //change camera button and popover
  const [anchorElCamera, setAnchorElCamera] =
    useState<HTMLButtonElement | null>(null);
  const handleClickCamera = (event: MouseEvent<HTMLButtonElement>) => {
    if (!pinCamera && !anchorElCamera) {
      setAnchorElCamera(event.currentTarget);
    }
  };
  const handleCloseCamera = () => {
    if (!pinCamera) {
      setAnchorElCamera(null);
    }
  };
  const cameraPopover = pinCamera ? pinCamera : Boolean(anchorElCamera);

  // handle buttons functions
  // play or pause view
  const handlePlayStop = () => {
    setPausePlay(!pausePlay);
    if (viewer !== undefined) {
      if (pausePlay) {
        viewer.stop();
      } else {
        viewer.play();
      }
    }
  };

  // move one step backward
  const handleStepBackward = () => {
    if (viewer !== undefined) {
      viewer.moveDirection(1);
    }
  };

  // move one step forward
  const handleStepForward = () => {
    if (viewer !== undefined) {
      viewer.moveDirection(0);
    }
  };

  // change speed of moving around
  const handleChangeSpeed = (value: number) => {
    setSpeedValue(value / 100);
    if (viewer !== undefined) {
      viewer.setSpeed(value / 100);
    }
  };

  // change direction of moving
  const handlePlayDirection = () => {
    if (viewer !== undefined) {
      if (directionBackward) {
        viewer.setDirection(0);
      } else {
        viewer.setDirection(1);
      }
    }
    setDirectionBackward(!directionBackward);
  };

  //change brightness level
  const handleChangeBrightness = (value: number) => {
    setBrightnessValue((value / 100) * 3);
    if (viewer !== undefined) {
      viewer.getScene().setGamma((value / 100) * 3);
    }
  };

  //reset brightness to default value by doubleclick
  const handleResetBrightness = () => {
    setBrightnessValue(1);
    viewer?.getScene().setGamma(1);
  };

  // reset speed to default value by doubleclick
  const handleResetSpeed = () => {
    setSpeedValue(0.62);
    viewer?.setSpeed(0.62);
  };

  function handlePointerEvent(e: React.PointerEvent) {
    if (!viewer) return;

    const canvas = viewer.getCanvas();
    const nativeEvent = e.nativeEvent;

    // Capture the pointer on the canvas for the entire pointer interaction
    if (nativeEvent.type === "pointerdown") {
      canvas.setPointerCapture(nativeEvent.pointerId);
    }

    // Build PointerEventInit by copying relevant properties safely
    const eventInit: PointerEventInit = {
      bubbles: true,
      cancelable: true,
      composed: true,
      pointerId: nativeEvent.pointerId,
      pointerType: nativeEvent.pointerType,
      clientX: nativeEvent.clientX,
      clientY: nativeEvent.clientY,
      screenX: nativeEvent.screenX,
      screenY: nativeEvent.screenY,
      buttons: nativeEvent.buttons,
      pressure: nativeEvent.pressure,
      isPrimary: nativeEvent.isPrimary,
      width: nativeEvent.width,
      height: nativeEvent.height,
      tiltX: nativeEvent.tiltX,
      tiltY: nativeEvent.tiltY,
      twist: nativeEvent.twist,
    };

    // Create and dispatch the synthetic PointerEvent on canvas
    const forwardedEvent = new PointerEvent(e.type, eventInit);
    canvas.dispatchEvent(forwardedEvent);
  }

  return (
    <Box
      id="cockpit"
      sx={{
        bottom: cockpitPosition ? "auto" : 32,
        display: showStreetMap ? "none" : "block",
        left: 0,
        marginX: "auto",
        position: "absolute",
        right: 0,
        top: cockpitPosition ? 16 : "auto",
        width: "fit-content",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(5, 57, 82, 0.4)",
          borderRadius: "16px",
          borderTop: "1px rgba(255,255,255,70%) solid",
          boxShadow: "0 6px 6px -6px rgba(0,0,0,50)",
          display: "flex",
          flexDirection: "row",
          padding: 1,
          position: "relative",
        }}
      >
        <Box
          id="cockpit-controls-left"
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Tooltip
            title={t("tooltip.cockpit_direction")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
          >
            <IconButton
              disabled={pausePlay}
              onClick={() =>
                monitor(
                  apm,
                  "change-play-direction | Cockpit",
                  handlePlayDirection
                )
              }
              disableRipple={true}
              sx={{
                color: directionBackward ? "secondary.main" : "white",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
                ":disabled": {
                  color: directionBackward ? "secondary.main" : "white",
                },
              }}
            >
              <Typography
                sx={{
                  paddingY: "2px",
                  fontWeight: 700,
                }}
              >
                R
              </Typography>
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t("tooltip.cockpit_back")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 10], // Adjusts the tooltip spacing
                  },
                },
              ],
            }}
          >
            <IconButton
              disabled={pausePlay}
              sx={{
                color: "white",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
                ":disabled": {
                  color: "white",
                },
              }}
              onClick={() =>
                monitor(apm, "move-step-backward | Cockpit", handleStepBackward)
              }
              disableRipple={true}
            >
              <NavigateBefore
                sx={{
                  fontSize: "28px",
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t("tooltip.cockpit_play")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
          >
            <IconButton
              sx={{
                color: "white",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
                width: "44px",
                height: "44px",
                overflowL: "hidden",
              }}
              disableRipple={true}
              onClick={() =>
                monitor(apm, "change-play-stop | Cockpit", handlePlayStop)
              }
            >
              {!pausePlay && (
                <PlayCircleOutline
                  sx={{
                    fontSize: "40px",
                  }}
                />
              )}
              {pausePlay && (
                <PauseCircleOutline
                  sx={{
                    fontSize: "40px",
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t("tooltip.cockpit_next")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
          >
            <IconButton
              disabled={pausePlay}
              sx={{
                color: "white",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
                ":disabled": {
                  color: "white",
                },
              }}
              onClick={() =>
                monitor(apm, "move-step-forward | Cockpit", handleStepForward)
              }
              disableRipple={true}
            >
              <NavigateNext
                sx={{
                  fontSize: "28px",
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t("tooltip.cockpit_speed")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
          >
            <IconButton
              disabled={pausePlay}
              sx={{
                color: speedPopover ? "secondary.main" : "white",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
                ":disabled": {
                  color: "white",
                },
              }}
              onClick={(event) =>
                monitor(apm, "open-speed-slider | Cockpit", () =>
                  handleClickSpeed(event)
                )
              }
              onDoubleClick={() =>
                monitor(apm, "reset-speed | Cockpit", handleResetSpeed)
              }
              disableRipple={true}
            >
              <Speed
                sx={{
                  fontSize: "28px",
                }}
              />
            </IconButton>
          </Tooltip>
          <PopoverWithPointerForwarding
            onDoubleClick={handleResetSpeed}
            open={speedPopover && !pausePlay}
            onClose={handleCloseSpeed}
            anchorEl={anchorElSpeed}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            classes={{
              paper: "infra3d-sliderPaper",
              root: cockpitPosition
                ? "infra3d-sliderRootBottom"
                : "infra3d-sliderRootTop",
            }}
          >
            <Slider
              sx={{
                height: "100px",
                color: "white",
              }}
              min={0.01}
              orientation="vertical"
              value={speedValue * 100}
              onChange={(event: Event) =>
                handleChangeSpeed(
                  parseFloat((event.target as HTMLInputElement).value)
                )
              }
              classes={{
                thumb: "infra3d-thumbSlider",
                track: "infra3d-trackSlider",
                rail: "infra3d-railSlider",
              }}
            />
          </PopoverWithPointerForwarding>
        </Box>
        <Axisscale />
        <Box
          id="cockpit-controls-right"
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Tooltip
            title={t("tooltip.cockpit_brightness")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
          >
            <IconButton
              disabled={pausePlay}
              onDoubleClick={() =>
                monitor(apm, "reset-contrast | Cockpit", handleResetBrightness)
              }
              onClick={(event) =>
                monitor(apm, "open-contrast-slider | Cockpit", () =>
                  handleClickContrast(event)
                )
              }
              disableRipple={true}
              sx={{
                color: contrastPopover ? "secondary.main" : "white",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
                ":disabled": {
                  color: "white",
                },
              }}
            >
              <Contrast
                sx={{
                  rotate: contrastPopover ? "-90deg" : "0deg",
                  transition: "rotate 0.3s",
                  fontSize: "28px",
                }}
              />
            </IconButton>
          </Tooltip>
          <PopoverWithPointerForwarding
            onDoubleClick={handleResetBrightness}
            open={contrastPopover && !pausePlay}
            onClose={handleCloseContrast}
            anchorEl={anchorElContrast}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            classes={{
              paper: "infra3d-sliderPaper",
              root: cockpitPosition
                ? "infra3d-sliderRootBottom"
                : "infra3d-sliderRootTop",
            }}
          >
            <Slider
              sx={{
                height: "100px",
                color: "white",
              }}
              value={(brightnessValue / 3) * 100}
              onChange={(event: Event) =>
                handleChangeBrightness(
                  parseFloat((event.target as HTMLInputElement).value)
                )
              }
              orientation="vertical"
              classes={{
                thumb: "infra3d-thumbSlider",
                track: "infra3d-trackSlider",
                rail: "infra3d-railSlider",
              }}
            />
          </PopoverWithPointerForwarding>
          <Tooltip
            title={t("tooltip.cockpit_camera")}
            placement="top"
            enterDelay={400}
            classes={{
              tooltip: "infra3d-buttonDescription",
              popper: "infra3d-buttonDescriptionPopperTop",
            }}
          >
            <IconButton
              disabled={pausePlay}
              sx={{
                color: cameraPopover ? "secondary.main" : "white",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
                ":disabled": {
                  color: "white",
                },
              }}
              onClick={(event) =>
                monitor(apm, "open-sensor-selection | Cockpit", () =>
                  handleClickCamera(event)
                )
              }
              disableRipple={true}
            >
              <CockpitCameraSelection />
            </IconButton>
          </Tooltip>
          <PopoverWithPointerForwarding
            open={(cameraPopover || pinCamera) && !pausePlay}
            anchorEl={anchorElCamera}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            onClose={handleCloseCamera}
            classes={{
              paper: pinCamera
                ? "infra3d-cameraPaper pinCamera"
                : "infra3d-cameraPaper",
              root: cockpitPosition
                ? pinCamera
                  ? "infra3d-cameraRootBottom pinRootCamera"
                  : "infra3d-cameraRootBottom"
                : pinCamera
                  ? "infra3d-cameraRootTop pinRootCamera"
                  : "infra3d-cameraRootTop",
            }}
          >
            <IconButton
              sx={{
                position: "absolute",
                top: "4px",
                right: "4px",
                rotate: "35deg",
                color: "white",
                zIndex: 1,
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
              }}
              size="small"
              onClick={() => setPinCamera(!pinCamera)}
            >
              {pinCamera ? <PushPin /> : <PushPinOutlined />}
            </IconButton>
            <CameraPositions
              camerasArray={camerasArray}
              activeCamera={activeCamera}
            />
            {camerasArray.length === 0 && skeleton()}
          </PopoverWithPointerForwarding>
        </Box>
      </Box>
    </Box>
  );
}
export const skeleton = () => {
  return (
    <Box
      sx={{
        position: "relative",
        width: "150px",
        height: "150px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Skeleton
        variant="circular"
        width="80px"
        height="80px"
        sx={{ backgroundColor: "whiteBackground.main" }}
      />
      <Skeleton
        variant="circular"
        width="40%"
        height="40%"
        animation={false}
        sx={{
          backgroundColor: "whiteBackground.main",
          position: "absolute",
          top: "30%",
          left: "30%",
        }}
      />
    </Box>
  );
};
